'use client';

import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import { toast } from '@/hooks/shared/use-toast';
import { DataLayerToolbar as SensorChartToolbar } from './toolbar/DataLayerToolbar';
import { DataLayerChartView as SensorSectionChartView } from './charts/DataLayerChartView';
import type { WaveformControls } from '@/components/pages/AssetsStudioPage/MainContent/SensorData/Waveform/components';
import { createExcelExport } from '@/hooks/shared/DataLayers';
import type { VerticalLine } from '../types';
import { generateTestId } from '@/utils/shared/generateTestIds';
import { filterVerticalLinesForSection } from '@/utils/expandableLegend.utils';

interface DataLayerSectionProps {
  // Section identifier
  sectionKey: string;

  // Configuration and data
  sectionConfig: any;
  activeFeatures: any;
  data: any;
  sectionConfigGraphs: any[];
  masterSensorColorMap?: Record<string, string>;
  dataLayer?: string; // Optional data layer identifier (e.g., 'spectrum_analysis', 'sensors')

  // State
  enabledSections: any;
  enabledSensors: any;
  expandedSections: any;
  showModal: any;
  splitCharts: any;
  loadingFeatures: any;
  sharedTimeRange: any;
  selectionMode: boolean;
  selectedRanges: any[];
  verticalLines: VerticalLine[];
  verticalLinesWithHighlight: VerticalLine[];
  intervalLines: any[];
  loadingLines: Set<string>;
  focusedSensors: Record<string, string>;
  focusMode: boolean;
  syncSectionLegends?: boolean;
  syncLegendComparisonTable?: boolean;

  // Harmonic/Side Bands state
  harmonicSelectionModeByChart: Record<string, boolean>;
  harmonicSelectionsByChart: Record<string, any[]>;
  focusedHarmonicByChart: Record<string, string | null>;
  benchmarkLineBySection: Record<string, string | null>;
  selectedHarmonicLineId: string | null;

  // Percentage/Table view state
  showPercentage: boolean;
  showPercentageBySection: Record<string, boolean>;
  showTableView: boolean;
  showTableViewBySection: Record<string, boolean>;
  setShowPercentage?: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPercentageBySection?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setShowTableView?: React.Dispatch<React.SetStateAction<boolean>>;
  setShowTableViewBySection?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

  // Selected timestamps for Excel export
  selectedTimestampsSet?: Set<any>;
  selectedMarkerTimestamp?: string | null;

  // Expandable legend
  expandableLegendSeries?: any;
  expandableLegendFocusMode?: boolean;
  enabledSeries?: Set<string>;
  popoverTimestamps?: any;
  onToggleExpandableSeries?: (seriesId: string) => void;
  onToggleExpandableSeriesFocus?: (seriesId: string) => void;
  onToggleExpandableTimestamp?: (seriesId: string, timestampId: string) => void;
  onRemoveMarker?: (markerId: string) => void;

  // Functions/Callbacks
  metricsFromFeatures: (sectionKey: string, sectionConfig: any, activeFeatures: any) => any;
  isSectionLoading: (sectionKey: string) => boolean;
  getSectionMultiSelect: (sectionKey: string) => boolean;
  sectionHasFeatures: (sectionKey: string) => boolean;
  toggleSection: (sectionKey: string) => void;
  toggleSectionMultiSelect: (sectionKey: string) => void;
  handleFeatureChangeWithRefetch: (featureKey: string, isActive: boolean, sectionKey: string) => Promise<void>;
  toggleSplitCharts: (sectionKey: string) => void;
  clearAllLines: () => void;
  handleShowModal: (section: string, enabled: boolean) => void;
  handleShowIntervalDialog: (sectionKey: string) => void;
  clearIntervalLines: () => void;
  handleToggleSensor: (sectionType: string, legendItem: any) => void;
  handleSensorAddition: (sectionType: string, value: string) => void;
  isSensorLoadingFromFetch: (sectionKey: string, sensorName: string) => boolean;
  hasCachedDataForSensor?: (sectionId: string, sensorName: string) => boolean;
  loadingSensors?: Set<string>;
  isTimestampsLoading?: boolean;
  addVerticalLine: (...args: any[]) => void;
  removeVerticalLine: (lineId: string) => void;
  handleRangeChange: (range: any) => void;
  handleReset: (sectionKey: string) => void;
  handleClearAll: (sectionKey: string) => void;
  handleEditThresholds: (sectionKey: string) => void;
  removeIntervalLine: (lineId: string) => void;
  handleAddSelectedRange: (startTime: Date, endTime: Date, chartId: string) => void;
  handleRemoveSelectedRange: (id: string) => void;
  cancelSectionRequest: (sectionKey: string) => void;
  addIntervalLines: (lines: any[]) => void;
  onFocus?: (sensorName: string, sectionId?: string) => void;
  onToggleFocusMode?: (sectionId: string) => void;

  // Harmonic/Side Bands callbacks
  setHarmonicSelectionsByChart: React.Dispatch<React.SetStateAction<Record<string, any[]>>>;
  setHarmonicSelectionModeByChart: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setSideBandsSelectionModeByChart: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setMarkerSelectionModeByChart: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  markerSelectionModeByChart: Record<string, boolean>;
  sideBandsSelectionModeByChart: Record<string, boolean>;
  customLineModeByChart: Record<string, string | null>;
  setCustomLineModeByChart: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;

  // Feature flags
  enableCompareTable?: boolean;
  enableMarkers?: boolean;
  enableHarmonics?: boolean;
  enableSideBands?: boolean;
  enableDrawing?: boolean;
  enableHarmonicEdit?: boolean;
  enableSideBandsEdit?: boolean;
  replaceMarkersOnHarmonicCreation?: boolean;
  markerLabel?: string;
  expandableLegendLabel?: string;
  showFloatingToolbar?: boolean;
  showInlineFocusButton?: boolean; // Control visibility of inline "Focus" button next to each sensor (default: true)
  syncTools?: {
    markers?: boolean;
    harmonics?: boolean;
    sideBands?: boolean;
  };
  showSectionControls?: boolean;
  isExpandable?: boolean;

  // Utility functions
  getHarmonicGroupCountCallback: (sectionKey: string) => number;
  getAvailableHarmonicPrefixesCallback: (sectionKey: string) => string[];
  getSideBandsGroupCountCallback: (sectionKey: string) => number;
  getAvailableSideBandsPrefixesCallback: (sectionKey: string) => string[];
  getHarmonicIntervalCallback: (sectionKey: string, xAxisType: 'date' | 'numeric' | 'category') => any;

  // Vertical line menu state
  pendingHarmonicCreation?: any | null;
  setSelectedHarmonicLineId: React.Dispatch<React.SetStateAction<string | null>>;
  setHarmonicMenuPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setSelectedVerticalLineId: React.Dispatch<React.SetStateAction<string | null>>;
  setVerticalLineMenuPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setClickedChartId: React.Dispatch<React.SetStateAction<string | null>>;
  setEditingHarmonicGroup: React.Dispatch<React.SetStateAction<any | null>>;
  setEditingSideBandsGroup: React.Dispatch<React.SetStateAction<any | null>>;
  setPendingHarmonicCreation: React.Dispatch<React.SetStateAction<any | null>>;
  setBenchmarkLineBySection: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  setFocusedHarmonicByChart: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;

  // Custom vertical line menu state
  setCustomLineMenuState?: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      position: { x: number; y: number };
      lineData: any;
    }>
  >;
  focusedCustomGroupId?: string | null;

  // Y-axis scale options
  showYAxisScale?: boolean;
  enableDynamicMargin?: boolean;
  autoscaleMode?: 'none' | 'onload' | 'onFirstDataLoad' | 'onDataLoad';
  autoscaleAxis?: 'x' | 'y' | 'both';
  // Waveform timestamp display
  waveformRequestedTimestamp?: string;
  waveformActualTimestamp?: string;
}

/**
 * DataLayerSection - Presentational component for rendering a single data layer section
 * Extracted from DataLayerDashboardContainer for better code organization
 * Updated with all latest features including highlighted lines, expandable legend, master colors
 */
export const DataLayerSection: React.FC<DataLayerSectionProps> = ({
  sectionKey,
  sectionConfig,
  activeFeatures,
  data,
  sectionConfigGraphs,
  masterSensorColorMap,
  dataLayer,
  enabledSections,
  enabledSensors,
  expandedSections,
  showModal,
  splitCharts,
  loadingFeatures,
  sharedTimeRange,
  selectionMode,
  selectedRanges,
  verticalLines,
  verticalLinesWithHighlight,
  intervalLines,
  loadingLines,
  focusedSensors,
  focusMode,
  syncSectionLegends,
  syncLegendComparisonTable,
  harmonicSelectionModeByChart,
  harmonicSelectionsByChart,
  focusedHarmonicByChart,
  benchmarkLineBySection,
  selectedHarmonicLineId,
  showPercentage,
  showPercentageBySection,
  showTableView,
  showTableViewBySection,
  setShowPercentage,
  setShowPercentageBySection,
  setShowTableView,
  setShowTableViewBySection,
  selectedTimestampsSet,
  selectedMarkerTimestamp,
  expandableLegendSeries,
  expandableLegendFocusMode,
  enabledSeries,
  popoverTimestamps,
  onToggleExpandableSeries,
  onToggleExpandableSeriesFocus,
  onToggleExpandableTimestamp,
  onRemoveMarker,
  metricsFromFeatures,
  isSectionLoading,
  getSectionMultiSelect,
  sectionHasFeatures,
  toggleSection,
  toggleSectionMultiSelect,
  handleFeatureChangeWithRefetch,
  toggleSplitCharts,
  clearAllLines,
  handleShowModal,
  handleShowIntervalDialog,
  clearIntervalLines,
  handleToggleSensor,
  handleSensorAddition,
  isSensorLoadingFromFetch,
  hasCachedDataForSensor,
  loadingSensors,
  isTimestampsLoading,
  addVerticalLine,
  removeVerticalLine,
  handleRangeChange,
  handleReset,
  handleClearAll,
  handleEditThresholds,
  removeIntervalLine,
  handleAddSelectedRange,
  handleRemoveSelectedRange,
  cancelSectionRequest,
  addIntervalLines,
  onFocus,
  onToggleFocusMode,
  setHarmonicSelectionsByChart,
  setHarmonicSelectionModeByChart,
  setSideBandsSelectionModeByChart,
  setMarkerSelectionModeByChart,
  markerSelectionModeByChart,
  sideBandsSelectionModeByChart,
  customLineModeByChart,
  setCustomLineModeByChart,
  enableCompareTable,
  enableMarkers,
  enableHarmonics,
  enableSideBands,
  enableDrawing,
  enableHarmonicEdit,
  enableSideBandsEdit,
  replaceMarkersOnHarmonicCreation,
  markerLabel,
  expandableLegendLabel,
  showFloatingToolbar,
  showInlineFocusButton = true,
  syncTools,
  getHarmonicGroupCountCallback,
  getAvailableHarmonicPrefixesCallback,
  getSideBandsGroupCountCallback,
  getAvailableSideBandsPrefixesCallback,
  getHarmonicIntervalCallback,
  pendingHarmonicCreation,
  setSelectedHarmonicLineId,
  setHarmonicMenuPosition,
  setSelectedVerticalLineId,
  setVerticalLineMenuPosition,
  setClickedChartId,
  setEditingHarmonicGroup,
  setEditingSideBandsGroup,
  setPendingHarmonicCreation,
  setBenchmarkLineBySection,
  setFocusedHarmonicByChart,
  showSectionControls,
  isExpandable,
  setCustomLineMenuState,
  focusedCustomGroupId,
  showYAxisScale = false,
  enableDynamicMargin = false,
  autoscaleMode = 'none',
  autoscaleAxis = 'both',
  waveformRequestedTimestamp,
  waveformActualTimestamp,
}) => {
  const baseMetricsFromFeatures = metricsFromFeatures(sectionKey, sectionConfig, activeFeatures);
  const isExpanded = expandedSections[sectionKey];
  const showComparisonTable = showModal[sectionKey] || false;
  const isSplit = splitCharts[sectionKey];
  const isDiscrete = sectionConfig[sectionKey]?.isDiscrete;
  const actualData = data[sectionKey];

  // DEBUG logs removed for performance

  const hasValidData =
    actualData &&
    ((actualData.data && actualData.data.length > 0) || (actualData.legend && actualData.legend.length > 0));

  const isSectionLoadingState = isSectionLoading(sectionKey);

  // Waveform state (for waveform data layer toolbar controls)
  const isWaveformDataLayer = sectionKey.toLowerCase().includes('waveform');
  const [waveformControls, setWaveformControls] = useState<WaveformControls | null>(null);
  const [waveformPlayMode, setWaveformPlayMode] = useState<'acceleration' | 'velocity'>('acceleration');
  const [isFullWaveformMode, setIsFullWaveformMode] = useState(false);

  const handleWaveformControlsReady = useCallback((controls: WaveformControls | null) => {
    setWaveformControls(controls);
  }, []);

  const handleWaveformPlayModeChange = useCallback((mode: 'acceleration' | 'velocity') => {
    setWaveformPlayMode(mode);
  }, []);
  const hasFeatureLoading = Object.values(loadingFeatures[sectionKey] || {}).some(Boolean);

  // SYNC MODE: Check if ANY section is loading
  // IMPORTANT: We want synced loading only for global changes (filters, query params),
  // NOT for individual sensor additions which should be per-section
  let shouldShowLoadingForThisSection = isSectionLoadingState || hasFeatureLoading;
  if (syncSectionLegends) {
    // In sync mode, only sync loading when ALL sections are loading (indicates global change)
    // If only some sections are loading, it's a per-section operation (like adding a sensor)
    const allSections = Object.keys(sectionConfig);
    const loadingSections = allSections.filter((section) => {
      return isSectionLoading(section) || Object.values(loadingFeatures[section] || {}).some(Boolean);
    });

    // Only sync loading state if ALL sections are loading (global operation)
    // OR if THIS specific section is loading (per-section operation)
    if (loadingSections.length === allSections.length) {
      // All sections loading = global operation, sync loading state
      shouldShowLoadingForThisSection = true;
    } else {
      // Some sections loading = per-section operation, use individual section state
      shouldShowLoadingForThisSection = isSectionLoadingState || hasFeatureLoading;
    }
  }

  const config = sectionConfig[sectionKey];

  // Resolve autoscale mode with inheritance:
  // Priority: graph-level > data layer-level > prop > 'none'
  // For split charts, each graph can have its own autoscale setting
  // For combined charts, use the data layer autoscale setting
  const dataLayerAutoscale = config?.autoscale;

  // Get graph-level autoscale (first graph's setting that has autoscale defined)
  const graphAutoscale = sectionConfigGraphs?.find((g: any) => g.autoscale !== undefined)?.autoscale;

  // Resolved autoscale: graph overrides data layer, then prop, then default 'none'
  const resolvedAutoscaleMode = graphAutoscale ?? dataLayerAutoscale ?? autoscaleMode ?? 'none';

  // Resolve autoscaleAxis with same inheritance pattern
  const dataLayerAutoscaleAxis = config?.autoscaleAxis;
  const graphAutoscaleAxis = sectionConfigGraphs?.find((g: any) => g.autoscaleAxis !== undefined)?.autoscaleAxis;
  const resolvedAutoscaleAxis = graphAutoscaleAxis ?? dataLayerAutoscaleAxis ?? autoscaleAxis ?? 'y';

  // SYNC MODE: Use merged state across ALL sections
  let allEnabled: Record<string, boolean> = {};

  if (syncSectionLegends) {
    // In sync mode, merge ALL sections' states into ONE shared state
    // Use OR logic: if ANY section has the sensor enabled, show as enabled
    const mergedState: Record<string, boolean> = {};
    Object.keys(enabledSensors).forEach((section) => {
      Object.entries(enabledSensors[section] || {}).forEach(([sensorName, isEnabled]) => {
        // If sensor is enabled in ANY section, consider it enabled
        mergedState[sensorName] = mergedState[sensorName] || (isEnabled as boolean);
      });
    });
    // IMPORTANT: Only include sensors that belong to THIS section (from config.sensorUiNames)
    // This ensures each section only shows its own sensors in the legend, even in sync mode
    const sectionSensorNames = config?.sensorUiNames || [];
    if (sectionSensorNames.length > 0) {
      sectionSensorNames.forEach((name: string) => {
        allEnabled[name] = mergedState[name] ?? enabledSensors[sectionKey]?.[name] ?? false;
      });
    } else {
      // Fallback for sections without sensorUiNames - use full merged state
      allEnabled = mergedState;
    }
  } else {
    // Non-sync mode: Use section-specific state
    const enabled = enabledSensors[sectionKey as keyof typeof enabledSensors];
    allEnabled = enabledSensors?.[sectionKey] || {};
    config?.sensorUiNames?.forEach((name: string) => {
      if (enabled && typeof enabled[name] === 'boolean') {
        allEnabled[name] = enabled[name];
      }
    });
  }

  // Memoize sensor names and colors to prevent unnecessary re-renders
  const allSensorNames = useMemo(() => {
    const names: string[] = [];
    config?.sensorUiNames?.forEach((name: string) => {
      names.push(name);
    });
    return names;
  }, [config?.sensorUiNames]);

  const allColors = useMemo(() => {
    // Default color palette to use when config.colors is insufficient
    const defaultPalette = [
      '#5ed3f3',
      '#6667dd',
      '#0acdba',
      '#ff80b5',
      '#ffb28f',
      '#ffd066',
      '#faa882',
      '#ffd576',
      '#fe93bf',
      '#6162d7',
      '#ffc84c',
      '#2accf8',
      '#4f50e1',
      '#0f988a',
      '#fcad89',
      '#3d3edf',
      '#02ccb8',
      '#ffabce',
      '#15edd8',
      '#86e1f9',
      '#FF7043',
      '#FF5722',
      '#795548',
      '#607D8B',
      '#006064',
      '#01579B',
      '#1A237E',
      '#311B92',
      '#4A148C',
      '#880E4F',
      '#B71C1C',
      '#E65100',
      '#F57F17',
      '#827717',
      '#33691E',
      '#1B5E20',
      '#004D40',
      '#00cfff',
      '#00b0ff',
      '#00acc1',
      '#2196f3',
      '#3d5afe',
      '#536dfe',
      '#5c6bc0',
      '#7c4dff',
      '#ab47bc',
      '#9c27b0',
      '#ec407a',
      '#e91e63',
      '#f44336',
      '#ff7043',
      '#ffa726',
      '#ffca28',
      '#ffeb3b',
      '#d4e157',
      '#aed581',
      '#4db6ac',
      '#26a69a',
      '#00e5ff',
      '#00bfa5',
      '#0097a7',
      '#26c6da',
      '#4fc3f7',
      '#7986cb',
      '#9fa8da',
      '#82b1ff',
      // Additional 100 colors
      '#64b5f6',
      '#4dd0e1',
      '#b39ddb',
      '#f06292',
      '#f48fb1',
      '#ff8a65',
      '#ffb74d',
      '#fff176',
      '#dce775',
      '#81c784',
      '#80cbc4',
      '#80deea',
      '#7986cb',
      '#9575cd',
      '#ba68c8',
      '#ce93d8',
      '#e57373',
      '#ff8a65',
      '#ffb74d',
      '#fff176',
      '#dce775',
      '#aed581',
      '#4dd0e1',
      '#64b5f6',
      '#9575cd',
      '#ba68c8',
      '#f06292',
      '#e57373',
      '#ff8a65',
      '#ffb74d',
      '#fff176',
      '#dce775',
      '#aed581',
      '#80deea',
      '#4fc3f7',
      '#9575cd',
      '#ce93d8',
      '#f06292',
      '#ff8a65',
      '#ffb74d',
      '#fff176',
      '#dce775',
      '#aed581',
      '#80deea',
      '#4fc3f7',
      '#ba68c8',
      '#f06292',
      '#e57373',
      '#ff8a65',
      '#ffb74d',
      '#fff176',
      '#dce775',
      '#aed581',
      '#42a5f5',
      '#26c6da',
      '#66bb6a',
      '#9ccc65',
      '#d4e157',
      '#ffee58',
      '#ffca28',
      '#ffa726',
      '#ff7043',
      '#8d6e63',
      '#bdbdbd',
      '#78909c',
      '#ef5350',
      '#ec407a',
      '#ab47bc',
      '#7e57c2',
      '#5c6bc0',
      '#42a5f5',
      '#29b6f6',
      '#26c6da',
      '#26a69a',
      '#66bb6a',
      '#9ccc65',
      '#d4e157',
      '#ffee58',
      '#ffca28',
      '#ffa726',
      '#ff7043',
      '#8d6e63',
      '#bdbdbd',
      '#78909c',
      '#b71c1c',
      '#880e4f',
      '#4a148c',
      '#311b92',
      '#1a237e',
      '#0d47a1',
      '#01579b',
      '#006064',
      '#004d40',
      '#1b5e20',
      '#33691e',
      '#827717',
      '#f57f17',
      '#ff6f00',
      '#e65100',
      '#bf360c',
      '#3e2723',
      '#212121',
      '#263238',
      '#c62828',
      '#ad1457',
      '#6a1b9a',
      '#4527a0',
      '#283593',
    ];

    const colors: string[] = [];
    config?.sensorUiNames?.forEach((name: string, i: number) => {
      // First priority: Use master color map for consistency across sections
      if (masterSensorColorMap?.[name]) {
        colors.push(masterSensorColorMap[name]);
      }
      // Second priority: Use config colors if available
      else if (config.colors && config.colors[i]) {
        colors.push(config.colors[i]);
      }
      // Third priority: Use default palette
      else if (defaultPalette[i]) {
        colors.push(defaultPalette[i]);
      }
      // Fallback: Use modulo on default palette
      else {
        colors.push(defaultPalette[i % defaultPalette.length]);
      }
    });
    return colors;
  }, [config?.sensorUiNames, config?.colors, masterSensorColorMap]);

  // CRITICAL: Convert from map format to per-section format
  // Check if THIS section has a focus
  let derivedFocusedSensor: string | { sensorName: string; sectionId: string } | null = null;

  if (syncSectionLegends) {
    // When legends are synced, check ANY section for focused sensor
    // and use it globally (as a string)
    const anyFocusedSensor = Object.values(focusedSensors || {}).find(Boolean);
    if (anyFocusedSensor) {
      derivedFocusedSensor = anyFocusedSensor;
    }
  } else {
    // When not synced, only use THIS section's focused sensor (as an object)
    const focusedSensorNameForThisSection = focusedSensors?.[sectionKey];
    if (focusedSensorNameForThisSection) {
      derivedFocusedSensor = {
        sensorName: focusedSensorNameForThisSection,
        sectionId: sectionKey,
      };
    }
  }

  const mergedConfig = useMemo(
    () => ({
      title: config?.title,
      unit: config?.unit || '',
      sensorUiNames: allSensorNames,
      colors: allColors,
    }),
    [config?.title, config?.unit, allSensorNames, allColors]
  );

  // Extract custom vertical lines from data (same as in DataLayerChartView)
  const customVerticalLines = data?.customVerticalLines;

  // Define custom line mode handler
  const onEnterCustomLineMode = (groupId: string) => {
    // ONLY ONE ACTIVE MODE: Clear all other selection modes first
    setMarkerSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));
    setHarmonicSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));
    setSideBandsSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));

    // Toggle behavior: If the same group is already active, deactivate it; otherwise, activate the new group
    setCustomLineModeByChart((prev: any) => {
      const currentMode = prev?.[sectionKey];
      const newMode = currentMode === groupId ? null : groupId; // Toggle off if same, otherwise set new group

      return {
        ...prev,
        [sectionKey]: newMode,
      };
    });
  };

  let featureOptions: {
    key: string;
    label: string;
    isActive: boolean;
  }[] = [];

  const hasFeatures = sectionHasFeatures(sectionKey);

  if (hasFeatures && config && config.features) {
    const sectionFeatures = activeFeatures[sectionKey as keyof typeof activeFeatures];

    featureOptions = Object.keys(config.features).map((featureKey: string) => {
      const feature = config.features[featureKey];
      const isActive = sectionFeatures?.[feature.id] || false;
      return {
        key: feature.id,
        label: feature.label,
        isActive: isActive,
      };
    });
  }

  const sectionMultiSelect = hasFeatures ? getSectionMultiSelect(sectionKey) : false;

  // Create sensor colors map with full palette
  const sensorColors = useMemo(() => {
    const colors: Record<string, string> = {};
    allSensorNames.forEach((name, i) => {
      colors[name] = allColors[i];
    });
    return colors;
  }, [allSensorNames, allColors]);

  // Override the sensorColors in metrics to use our full palette
  const metrics = useMemo(() => {
    return baseMetricsFromFeatures.map((metric: any) => ({
      ...metric,
      sensorColors: sensorColors, // Use the sensorColors map we created with full palette
    }));
  }, [baseMetricsFromFeatures, sensorColors]);

  // Determine x-axis type and label for export
  let xAxisType: 'date' | 'numeric' = 'date';
  let xAxisLabel = 'Time';

  if (actualData?.data?.[0]?.x && actualData.data[0].x.length > 0) {
    const firstX = actualData.data[0].x[0];
    if (typeof firstX === 'number') {
      xAxisType = 'numeric';
      // Try to determine label based on section title or unit
      if (config?.title?.toLowerCase().includes('spectrum')) {
        xAxisLabel = 'Frequency (Hz)';
      } else if (config?.title?.toLowerCase().includes('rpm')) {
        xAxisLabel = 'RPM';
      } else {
        xAxisLabel = config?.unit || 'Value';
      }
    }
  }

  // Convert selectedTimestampsSet to array of vertical line IDs
  const selectedIds: string[] = [];
  if (selectedTimestampsSet && selectedTimestampsSet.size > 0) {
    const selectedArray = Array.from(selectedTimestampsSet);
    verticalLinesWithHighlight.forEach((vl: VerticalLine) => {
      const vlTs = vl.timestamp;
      if (vlTs !== undefined && vlTs !== null) {
        const matched = selectedArray.some((selTs: any) => {
          return String(vlTs) === String(selTs) || Number(vlTs) === Number(selTs) || vlTs === selTs;
        });
        if (matched) {
          selectedIds.push(vl.id);
        }
      }
    });
  }

  // Use Excel export function
  const handleDownload = createExcelExport({
    sectionName: config?.title,
    sectionKey,
    sensors: allSensorNames,
    verticalLines: verticalLinesWithHighlight,
    enabledSensors: allEnabled,
    sensorColors,
    showPercentage,
    showPercentageBySection,
    benchmarkLineId: benchmarkLineBySection?.[sectionKey],
    benchmarkLineBySection,
    syncLegendComparisonTable,
    chartData: actualData,
    xAxisType,
    xAxisLabel,
    selectedVerticalLineIds: selectedIds,
    chartDivId: `chart-${sectionKey}-combined`, // ID of the Plotly chart div for image capture
  });

  return (
    <Card
      key={sectionKey}
      className="mb-4 bg-white dark:bg-[#52606d] border-[#e6e8ec] dark:border-gray-700"
      data-section-title={mergedConfig.title}
    >
      <CardHeader className="py-2 px-1.5">
        <SensorChartToolbar
          sectionKey={sectionKey}
          isExpanded={isExpanded}
          onToggleSection={toggleSection}
          isExpandable={isExpandable}
          title={mergedConfig.title}
          unit={mergedConfig.unit}
          showMultiSelect={false}
          multiSelect={sectionMultiSelect}
          onToggleMultiSelect={() => toggleSectionMultiSelect(sectionKey)}
          featureOptions={featureOptions}
          onFeatureChange={handleFeatureChangeWithRefetch}
          showSplitToggle={sectionConfig[sectionKey]?.splitToggle}
          isSplit={isSplit}
          onToggleSplitCharts={toggleSplitCharts}
          verticalLines={verticalLines}
          showComparisonTable={showComparisonTable}
          showCompareToggle={enableCompareTable}
          onToggleCompareModal={() => handleShowModal(sectionKey, !showComparisonTable)}
          onClearLines={clearAllLines}
          markingTools={{
            markers: enableMarkers,
            harmonics: enableHarmonics,
            sideBands: enableSideBands,
            drawing: enableDrawing,
          }}
          intervalLines={intervalLines}
          onAddIntervalLines={addIntervalLines}
          onClearIntervalLines={clearIntervalLines}
          xAxisData={Array.isArray(actualData) ? actualData.map((d: any) => d.timestamp) : []}
          xAxisType={(() => {
            // Detect x-axis type from actual data
            if (actualData?.data?.[0]?.x && actualData.data[0].x.length > 0) {
              const firstX = actualData.data[0].x[0];
              // If x value is a number (not a date string), it's numeric
              return typeof firstX === 'number' ? 'numeric' : 'date';
            }
            return 'date';
          })()}
          onEnterMarkerSelectionMode={() => {
            const isCurrentlyActive = markerSelectionModeByChart[sectionKey];
            const newValue = !isCurrentlyActive;

            // ONLY ONE ACTIVE MODE: Clear custom line mode when entering marker mode
            if (newValue) {
              setCustomLineModeByChart((prev: any) => ({ ...prev, [sectionKey]: null }));
            }

            if (syncTools?.markers) {
              // Synced mode: update ALL sections to the same state
              setMarkerSelectionModeByChart((prev) => {
                const newState: Record<string, boolean> = {};
                Object.keys(sectionConfig).forEach((key) => {
                  newState[key] = newValue;
                });
                return newState;
              });
              // Turn off other modes for all sections
              if (syncTools?.harmonics) {
                setHarmonicSelectionModeByChart({});
              }
              if (syncTools?.sideBands) {
                setSideBandsSelectionModeByChart({});
              }
            } else {
              // Per-section mode: toggle only this section
              setMarkerSelectionModeByChart((prev) => ({
                ...prev,
                [sectionKey]: newValue,
              }));
              // Turn off harmonics and side bands modes for this section
              setHarmonicSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));
              setSideBandsSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));
            }
          }}
          isMarkerSelectionMode={!!markerSelectionModeByChart[sectionKey]}
          markerLabel={markerLabel}
          customVerticalLines={actualData?.customVerticalLines}
          customLinesConfig={{ sync: false, enableDynamicButtons: false }}
          onEnterCustomLineMode={onEnterCustomLineMode}
          isCustomLineMode={customLineModeByChart?.[sectionKey] || null}
          dataLayer={
            dataLayer ||
            (() => {
              // Determine data layer based on section key if not provided
              // Temperature and Pressure sections are part of 'sensors' data layer
              if (
                sectionKey.includes('Temperature') ||
                sectionKey.includes('Pressure') ||
                sectionKey.includes('Vibration') ||
                sectionKey.includes('Flow')
              ) {
                return 'sensors';
              }
              if (sectionKey.includes('spectrum') || sectionKey.includes('Spectrum')) {
                return 'spectrum_analysis';
              }
              if (
                sectionKey.includes('leftRoller') ||
                sectionKey.includes('rightRoller') ||
                sectionKey.includes('rockSize')
              ) {
                return 'visualAI';
              }
              return 'sensors'; // fallback to sensors
            })()
          }
          chartId={`${sectionKey}-combined`} // Use section-based chartId
          harmonicSelections={harmonicSelectionsByChart[sectionKey] || []}
          onAddHarmonicSelection={(selection) => {
            // Use functional updates for immediate state change
            setHarmonicSelectionsByChart((prev) => ({
              ...prev,
              [sectionKey]: [...(prev[sectionKey] || []), selection],
            }));
            setHarmonicSelectionModeByChart({});
          }}
          onUpdateHarmonicSelection={(id, updates) => {
            const currentSelections = harmonicSelectionsByChart[sectionKey] || [];
            setHarmonicSelectionsByChart({
              ...harmonicSelectionsByChart,
              [sectionKey]: currentSelections.map((s: any) => (s.id === id ? { ...s, ...updates } : s)),
            });
          }}
          onRemoveHarmonicSelection={(id) => {
            const currentSelections = harmonicSelectionsByChart[sectionKey] || [];
            setHarmonicSelectionsByChart({
              ...harmonicSelectionsByChart,
              [sectionKey]: currentSelections.filter((s: any) => s.id !== id),
            });
          }}
          onRemoveAllHarmonics={() => {
            setHarmonicSelectionsByChart({
              ...harmonicSelectionsByChart,
              [sectionKey]: [],
            });
          }}
          onFocusHarmonic={(id) => {
            const currentSelections = harmonicSelectionsByChart[sectionKey] || [];
            setHarmonicSelectionsByChart({
              ...harmonicSelectionsByChart,
              [sectionKey]: currentSelections.map((s: any) => ({ ...s, isFocused: s.id === id })),
            });
          }}
          onResetHarmonic={(id) => {
            // Reset logic - implement as needed
          }}
          onEnterHarmonicSelectionMode={() => {
            const isCurrentlyActive = harmonicSelectionModeByChart[sectionKey];
            const newValue = !isCurrentlyActive;

            if (syncTools?.harmonics) {
              // Synced mode: update ALL sections to the same state
              setHarmonicSelectionModeByChart((prev) => {
                const newState: Record<string, boolean> = {};
                Object.keys(sectionConfig).forEach((key) => {
                  newState[key] = newValue;
                });
                return newState;
              });
              // Turn off other modes for all sections
              if (syncTools?.markers) {
                setMarkerSelectionModeByChart({});
              }
              if (syncTools?.sideBands) {
                setSideBandsSelectionModeByChart({});
              }
            } else {
              // Per-section mode: toggle only this section
              setHarmonicSelectionModeByChart((prev) => ({
                ...prev,
                [sectionKey]: newValue,
              }));
              // Turn off markers and side bands modes for this section
              setMarkerSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));
              setSideBandsSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));
            }
          }}
          isHarmonicSelectionMode={!!harmonicSelectionModeByChart[sectionKey]}
          onEnterSideBandsSelectionMode={() => {
            const isCurrentlyActive = sideBandsSelectionModeByChart[sectionKey];
            const newValue = !isCurrentlyActive;

            if (syncTools?.sideBands) {
              // Synced mode: update ALL sections to the same state
              setSideBandsSelectionModeByChart((prev) => {
                const newState: Record<string, boolean> = {};
                Object.keys(sectionConfig).forEach((key) => {
                  newState[key] = newValue;
                });
                return newState;
              });
              // Turn off other modes for all sections
              if (syncTools?.markers) {
                setMarkerSelectionModeByChart({});
              }
              if (syncTools?.harmonics) {
                setHarmonicSelectionModeByChart({});
              }
            } else {
              // Per-section mode: toggle only this section
              setSideBandsSelectionModeByChart((prev) => ({
                ...prev,
                [sectionKey]: newValue,
              }));
              // Turn off markers and harmonics modes for this section
              setMarkerSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));
              setHarmonicSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));
            }
          }}
          isSideBandsSelectionMode={!!sideBandsSelectionModeByChart[sectionKey]}
          loadingFeatures={loadingFeatures[sectionKey] || {}}
          isFeatureLoading={hasFeatureLoading || isSectionLoadingState}
          pendingHarmonicCreation={pendingHarmonicCreation?.sectionKey === sectionKey ? pendingHarmonicCreation : null}
          // Waveform controls
          isWaveformDataLayer={isWaveformDataLayer}
          waveformControls={waveformControls}
          waveformPlayMode={waveformPlayMode}
          onWaveformPlayModeChange={handleWaveformPlayModeChange}
          onDownloadFullWaveform={() => {
            // Toggle full waveform mode (5 sec preview vs 30 sec full)
            setIsFullWaveformMode((prev) => !prev);
          }}
          isFullWaveformMode={isFullWaveformMode}
          waveformInteractionMode={waveformControls?.interactionMode}
          onToggleWaveformInteractionMode={() => waveformControls?.toggleInteractionMode()}
          onResetWaveformZoom={() => waveformControls?.resetZoom()}
        />
      </CardHeader>
      {isExpanded && (
        <CardContent data-testid={`graph_section_${generateTestId(sectionKey)}`}>
          <SensorSectionChartView
            data={actualData}
            isLoadingState={shouldShowLoadingForThisSection}
            sharedTimeRange={sharedTimeRange}
            config={mergedConfig}
            handleToggleSensor={(legendItem: any) => handleToggleSensor(sectionKey, legendItem)}
            handleAddSensor={(value: string) => handleSensorAddition(sectionKey, value)}
            enabledSensors={allEnabled}
            isSplit={isSplit}
            splitColumns={sectionConfig?.[sectionKey]?.splitCol || 1}
            sectionId={sectionKey}
            thresholdVariant={sectionConfig?.[sectionKey]?.thresholdVariant}
            dataLayer={
              dataLayer ||
              (() => {
                // Determine data layer based on section key if not provided
                // Same logic as toolbar - Temperature/Pressure/Vibration are all 'sensors'
                if (
                  sectionKey.includes('Temperature') ||
                  sectionKey.includes('Pressure') ||
                  sectionKey.includes('Vibration') ||
                  sectionKey.includes('Flow')
                ) {
                  return 'sensors';
                }
                if (sectionKey.includes('spectrum') || sectionKey.includes('Spectrum')) {
                  return 'spectrum_analysis';
                }
                if (sectionKey.toLowerCase().includes('waveform')) {
                  return 'waveform';
                }
                if (
                  sectionKey.includes('leftRoller') ||
                  sectionKey.includes('rightRoller') ||
                  sectionKey.includes('rockSize')
                ) {
                  return 'visualAI';
                }
                return 'sensors';
              })()
            }
            isDiscrete={isDiscrete}
            metrics={metrics}
            addVerticalLine={addVerticalLine}
            focusedSensor={derivedFocusedSensor}
            focusMode={focusMode}
            selectionMode={selectionMode}
            selectedRanges={selectedRanges}
            verticalLines={filterVerticalLinesForSection(verticalLinesWithHighlight, sectionKey)}
            customVerticalLines={customVerticalLines}
            intervalLines={intervalLines}
            onVerticalLineClick={(lineId: string, position: { x: number; y: number }, chartId?: string) => {
              // Show menu for non-harmonic lines (compare/marker lines) OR harmonic starting points
              const line = verticalLines.find((l) => l.id === lineId);
              if (line && (!line.isHarmonic || line.isFirstHarmonic)) {
                setSelectedVerticalLineId(lineId);
                // Store the chart ID so we can use it when creating chart-specific harmonics/carriers
                setClickedChartId(chartId || null);
                // Show menu at the click position with slight offset to avoid covering the label
                setVerticalLineMenuPosition({ x: position.x + 10, y: position.y + 10 });
              }
            }}
            onHarmonicLineClick={(lineId: string, position: { x: number; y: number }) => {
              setSelectedHarmonicLineId(lineId);
              setHarmonicMenuPosition(position);
            }}
            handleRangeChange={handleRangeChange}
            handleReset={() => handleReset(sectionKey)}
            handelClearAll={() => handleClearAll(sectionKey)}
            handleEditThresholds={() => handleEditThresholds(sectionKey)}
            removeIntervalLine={removeIntervalLine}
            handleAddSelectedRange={handleAddSelectedRange}
            handleRemoveSelectedRange={handleRemoveSelectedRange}
            onFocus={onFocus}
            onToggleFocusMode={onToggleFocusMode}
            isSensorLoading={(sensorName: string) => isSensorLoadingFromFetch(sectionKey, sensorName)}
            hasCachedDataForSensor={
              hasCachedDataForSensor
                ? (sensorName: string) => hasCachedDataForSensor(sectionKey, sensorName)
                : undefined
            }
            loadingSensors={loadingSensors}
            isTimestampsLoading={isTimestampsLoading}
            onCancelSectionRequest={() => cancelSectionRequest(sectionKey)}
            loadingLineValues={loadingLines}
            syncSectionLegends={syncSectionLegends}
            selectedBenchmarkId={benchmarkLineBySection[sectionKey] || null}
            focusedHarmonicColor={(() => {
              const groupId = focusedHarmonicByChart[sectionKey] || null;
              return groupId;
            })()}
            selectedHarmonicLineId={selectedHarmonicLineId}
            isMarkerSelectionMode={!!markerSelectionModeByChart[sectionKey]}
            isHarmonicSelectionMode={!!harmonicSelectionModeByChart[sectionKey]}
            isSideBandsSelectionMode={!!sideBandsSelectionModeByChart[sectionKey]}
            pendingHarmonicCreation={
              pendingHarmonicCreation?.sectionKey === sectionKey ? pendingHarmonicCreation : null
            }
            onCancelPendingHarmonic={() => {
              setPendingHarmonicCreation(null);
            }}
            removeVerticalLine={removeVerticalLine}
            clearAllLines={clearAllLines}
            showComparisonTable={showComparisonTable}
            showPercentage={syncLegendComparisonTable ? showPercentage : showPercentageBySection[sectionKey] ?? true}
            showTableView={syncLegendComparisonTable ? showTableView : showTableViewBySection[sectionKey] ?? false}
            onToggleCompareModal={() => handleShowModal(sectionKey, !showComparisonTable)}
            onTogglePercentage={
              syncLegendComparisonTable
                ? (show: boolean) => {
                    // Global percentage toggle - affects all sections
                    setShowPercentage?.(show);
                  }
                : (show: boolean) => {
                    // Per-section percentage toggle
                    setShowPercentageBySection?.((prev) => ({
                      ...prev,
                      [sectionKey]: show,
                    }));
                  }
            }
            onToggleTableView={
              syncLegendComparisonTable
                ? (show: boolean) => {
                    // Global table view toggle - affects all sections
                    setShowTableView?.(show);
                  }
                : (show: boolean) => {
                    // Per-section table view toggle
                    setShowTableViewBySection?.((prev) => ({
                      ...prev,
                      [sectionKey]: show,
                    }));
                  }
            }
            onAddHarmonicSelection={(selection) => {
              // Get available harmonic prefixes (H, I, J that are NOT yet used)
              const availablePrefixes = getAvailableHarmonicPrefixesCallback(sectionKey);
              const currentHarmonicCount = getHarmonicGroupCountCallback(sectionKey);

              // Only allow up to 3 harmonic groups (H, I, J)
              if (availablePrefixes.length === 0 || currentHarmonicCount >= 3) {
                toast({
                  title: 'Maximum harmonic groups reached!',
                  description: `You can only add up to 3 harmonic groups (H, I, J) per section. Current harmonic groups: ${currentHarmonicCount}/3. To add more, please remove an existing harmonic group first.`,
                });
                // Exit harmonics mode
                setHarmonicSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));
                return;
              }

              // Use the FIRST available prefix (H first, then I, then J)
              const labelPrefix = availablePrefixes[0];

              // Default settings
              const defaultMultiplier = 6;
              const startingPoint = selection.hz;
              const xAxisType = selection.xAxisType || 'numeric';
              const chartId = selection.chartId;

              // Set group colors: each group gets progressively lighter
              // H = color 0, I = color 1, J = color 2
              const groupColors = ['#55687D', '#7B8A9D', '#9BA8B7'];
              const colorIndex = ['H', 'I', 'J'].indexOf(labelPrefix);
              const groupColor = groupColors[colorIndex] || '#55687D';

              // Generate unique group ID for this harmonic/side bands group
              const groupId = `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

              // Generate H1 at the clicked position (this is the base/fundamental frequency)
              // IMPORTANT: Harmonics start from 1, NOT 0
              const startLabel = `${labelPrefix}1`;
              addVerticalLine(
                xAxisType === 'date' ? new Date(startingPoint) : startingPoint,
                {},
                startingPoint,
                startLabel,
                groupColor,
                true,
                true,
                chartId,
                sectionKey,
                'system',
                labelPrefix,
                groupId
              );

              // Generate harmonic lines: H2, H3, H4, H5, H6
              // Start from 2 since we already created H1 as the starting point
              // If defaultMultiplier is 6, we create H1 + H2-H6 (5 additional) = 6 total
              // The spacing is calculated as the distance from 0 to H1
              // Each harmonic is placed at H1 + (spacing * (i-1))
              // Example: H1 at 100Hz → spacing = 100Hz → H2 at 200Hz, H3 at 300Hz, etc.
              const harmonicSpacing = Math.abs(startingPoint); // Distance from 0 to H1

              for (let i = 2; i <= defaultMultiplier; i++) {
                const harmonicValue = startingPoint + harmonicSpacing * (i - 1); // Add spacing for each harmonic
                const label = `${labelPrefix}${i}`;

                addVerticalLine(
                  xAxisType === 'date' ? new Date(harmonicValue) : harmonicValue,
                  {},
                  harmonicValue,
                  label,
                  groupColor,
                  true,
                  false,
                  chartId,
                  sectionKey,
                  'system',
                  labelPrefix,
                  groupId
                );
              }

              // Exit selection mode
              setHarmonicSelectionModeByChart((prev) => ({
                ...prev,
                [sectionKey]: false,
              }));
            }}
            onGenerateSideBandsFromClick={(
              startingPoint: number,
              xAxisType: 'date' | 'numeric' | 'category',
              chartId: string
            ) => {
              // Get available side bands prefixes (C, D, E that are NOT yet used)
              const availableSideBandsPrefixes = getAvailableSideBandsPrefixesCallback(sectionKey);
              const currentSideBandsCount = getSideBandsGroupCountCallback(sectionKey);

              // Only allow up to 3 side bands groups (C, D, E)
              if (availableSideBandsPrefixes.length === 0 || currentSideBandsCount >= 3) {
                toast({
                  title: 'Maximum side bands groups reached!',
                  description: `You can only add up to 3 carrier groups (C, D, E) per section. Current carrier groups: ${currentSideBandsCount}/3. To add more, please remove an existing carrier group first.`,
                });
                // Exit side bands mode
                setSideBandsSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));
                return;
              }

              // Use the FIRST available prefix (C first, then D, then E)
              const labelPrefix = availableSideBandsPrefixes[0];

              // Set group colors: C = color 0, D = color 1, E = color 2
              const groupColors = [
                '#55687D', // Group 1: Gray-blue
                '#7B8A9D', // Group 2: Lighter gray-blue
                '#9BA8B7', // Group 3: Even lighter
              ];
              const colorIndex = ['C', 'D', 'E'].indexOf(labelPrefix);
              const groupColor = groupColors[colorIndex] || '#55687D';

              // Generate unique group ID for this side bands group (same as harmonics)
              const groupId = `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

              // FIRST CLICK: Immediately create C0/D0/E0 (center line)

              // If replaceMarkersOnHarmonicCreation is true, remove all markers for this section
              if (replaceMarkersOnHarmonicCreation) {
                const markersToRemove = verticalLines.filter(
                  (line) => !line.isHarmonic && line.sectionKey === sectionKey
                );
                markersToRemove.forEach((line) => {
                  removeVerticalLine(line.id);
                });
              }

              addVerticalLine(
                startingPoint as any, // timestamp
                {}, // empty values - will be filled by handler
                startingPoint, // xValue
                `${labelPrefix}0`, // Label: C0, D0, or E0
                groupColor, // color
                true, // isHarmonic
                true, // isFirstHarmonic (center line)
                chartId, // chartId
                sectionKey, // sectionKey
                'system', // markerType
                labelPrefix, // originalPrefix
                groupId // groupId
              );

              // Enter pending mode for two-step side bands creation
              setPendingHarmonicCreation({
                centerPoint: startingPoint,
                chartId: chartId,
                sectionKey: sectionKey,
                type: 'sideBands',
                labelPrefix: labelPrefix,
                color: groupColor,
                xAxisType: xAxisType,
                groupId: groupId,
              });

              // Exit side bands selection mode (purple line will now show for second click)
              setSideBandsSelectionModeByChart((prev) => ({ ...prev, [sectionKey]: false }));
            }}
            onSetBenchmark={(lineId: string | null) => {
              if (syncLegendComparisonTable) {
                // Synced mode: Set benchmark for ALL sections with the same lineId
                // Since markers are now global (no sectionKey), the same marker is visible to all sections
                setBenchmarkLineBySection((prev) => {
                  const newState = { ...prev };
                  Object.keys(sectionConfig).forEach((key) => {
                    // Set the same lineId for all sections (or null to clear)
                    newState[key] = lineId;
                  });
                  return newState;
                });
              } else {
                // Per-section mode: Set benchmark only for this section
                setBenchmarkLineBySection((prev) => ({
                  ...prev,
                  [sectionKey]: lineId,
                }));
              }
            }}
            onFocusHarmonic={(groupId: string) => {
              setFocusedHarmonicByChart((prev) => ({
                ...prev,
                [sectionKey]: groupId,
              }));
            }}
            onUnfocusHarmonic={() => {
              setFocusedHarmonicByChart((prev) => ({
                ...prev,
                [sectionKey]: null,
              }));
            }}
            showDualLabels={false}
            selectedMarkerLabel={selectedMarkerTimestamp}
            selectedTimestampsSet={selectedTimestampsSet}
            expandableLegendSeries={expandableLegendSeries}
            expandableLegendFocusMode={expandableLegendFocusMode}
            enabledSeries={enabledSeries}
            popoverTimestamps={popoverTimestamps}
            onToggleExpandableSeries={onToggleExpandableSeries}
            onToggleExpandableSeriesFocus={onToggleExpandableSeriesFocus}
            onToggleExpandableTimestamp={onToggleExpandableTimestamp}
            onRemoveMarker={onRemoveMarker}
            markerLabel={markerLabel}
            expandableLegendLabel={expandableLegendLabel}
            showFloatingToolbar={showFloatingToolbar}
            showInlineFocusButton={showInlineFocusButton}
            showSectionControls={showSectionControls}
            isCustomLineMode={customLineModeByChart?.[sectionKey]}
            onEnterCustomLineMode={onEnterCustomLineMode}
            setCustomLineMenuState={setCustomLineMenuState}
            focusedCustomGroupId={focusedCustomGroupId}
            showYAxisScale={showYAxisScale}
            enableDynamicMargin={enableDynamicMargin}
            autoscaleMode={resolvedAutoscaleMode}
            autoscaleAxis={resolvedAutoscaleAxis}
            // Waveform callbacks
            onWaveformControlsReady={handleWaveformControlsReady}
            onWaveformPlayModeChange={handleWaveformPlayModeChange}
            waveformPlayMode={waveformPlayMode}
            isFullWaveformMode={isFullWaveformMode}
            waveformRequestedTimestamp={waveformRequestedTimestamp}
            waveformActualTimestamp={waveformActualTimestamp}
          />
        </CardContent>
      )}
    </Card>
  );
};
